import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Activity } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 flex-col justify-between p-12 border-r border-white/10 bg-grid">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-white text-black flex items-center justify-center">
            <Activity className="w-4 h-4" strokeWidth={2} />
          </div>
          <div className="text-sm font-semibold tracking-tight" style={{ fontFamily: "Outfit" }}>
            Digi-One
          </div>
        </div>
        <div className="max-w-md space-y-4">
          <div className="label-overline">Operations management</div>
          <h1 className="text-4xl font-semibold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>
            Run CDARK, assets and services from a single, premium console.
          </h1>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Track tasks, surface issues, manage teams, and keep your asset
            lifecycle data tidy — without leaving one screen.
          </p>
        </div>
        <div className="text-xs text-zinc-600">© Digi-One</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-7 h-7 rounded-md bg-white text-black flex items-center justify-center">
              <Activity className="w-4 h-4" />
            </div>
            <div className="text-sm font-semibold">Digi-One</div>
          </div>
          <div className="label-overline mb-2">Sign in</div>
          <h2 className="text-2xl font-semibold mb-1 tracking-tight" style={{ fontFamily: "Outfit" }}>
            Welcome back
          </h2>
          <p className="text-sm text-zinc-500 mb-8">Use your work email to access the console.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs text-zinc-400">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="bg-zinc-900 border-white/10 text-white"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs text-zinc-400">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-zinc-900 border-white/10 text-white"
                required
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-400" data-testid="login-error">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-zinc-200 font-medium"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in…" : (
                <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
